/**
 * Dynamic public web base for feedback / registration links.
 * Prefer REACT_APP_PUBLIC_WEB_URL, else derive from API host, else localhost:3000.
 */
export function getPublicWebBase(): string {
  const fromEnv = String(process.env.REACT_APP_PUBLIC_WEB_URL || "").trim();
  if (fromEnv) return fromEnv.replace(/\/$/, "");

  try {
    const urls = require("../../global/constants/UrlConstants").default;
    const api = String(urls?.url_prod || "");
    if (api.includes("localhost") || api.includes("127.0.0.1")) {
      return "http://localhost:3000";
    }
    if (/qa-api\.yhataw\.com/i.test(api)) {
      return "https://qa.yhataw.com";
    }
    if (/api\.yhataw\.com/i.test(api)) {
      return "https://www.yhataw.com";
    }
  } catch (_) {
    /* ignore */
  }
  return "http://localhost:3000";
}

export function getFeedbackPublicBase(): string {
  return getPublicWebBase();
}

/** @deprecated use getPublicWebBase() */
export const FEEDBACK_PUBLIC_BASE = getFeedbackPublicBase();

type PublicFormRow = {
  publicUrl?: string;
  orgSlug?: string;
  projectSlug?: string;
  publicSlug?: string;
  form_name?: string;
  _id?: string;
  formType?: string;
  defaultLeadSource?: string;
};

export type FormSharePlatformId =
  | "public"
  | "meta"
  | "instagram"
  | "google"
  | "youtube"
  | "linkedin"
  | "whatsapp"
  | "twitter";

export type FormSharePlatform = {
  id: FormSharePlatformId;
  label: string;
  hint: string;
  src: string;
  utmSource?: string;
  utmMedium?: string;
  color: string;
};

export type FormShareLink = FormSharePlatform & {
  url: string;
};

/** Paste these into ads / social — `src` + UTM tell CRM where the visit came from. */
export const FORM_SHARE_PLATFORMS: FormSharePlatform[] = [
  {
    id: "public",
    label: "Public link",
    hint: "Website, QR, or untagged share",
    src: "",
    color: "#1E3A5F",
  },
  {
    id: "meta",
    label: "Meta ads",
    hint: "Facebook / Instagram ads — paste as the landing URL in Ads Manager",
    src: "meta",
    utmSource: "facebook",
    utmMedium: "paid",
    color: "#1877F2",
  },
  {
    id: "instagram",
    label: "Instagram",
    hint: "Organic bio, stories, or posts (not paid ads)",
    src: "instagram",
    utmSource: "instagram",
    utmMedium: "social",
    color: "#E4405F",
  },
  {
    id: "google",
    label: "Google ads",
    hint: "Google Ads final URL / landing page",
    src: "google",
    utmSource: "google",
    utmMedium: "cpc",
    color: "#4285F4",
  },
  {
    id: "youtube",
    label: "YouTube",
    hint: "Video description or YouTube ads",
    src: "youtube",
    utmSource: "youtube",
    utmMedium: "video",
    color: "#FF0000",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    hint: "Posts, messages, or LinkedIn ads",
    src: "linkedin",
    utmSource: "linkedin",
    utmMedium: "social",
    color: "#0A66C2",
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    hint: "Broadcasts, status, or chat links",
    src: "whatsapp",
    utmSource: "whatsapp",
    utmMedium: "social",
    color: "#25D366",
  },
  {
    id: "twitter",
    label: "X / Twitter",
    hint: "Posts or ads on X",
    src: "twitter",
    utmSource: "twitter",
    utmMedium: "social",
    color: "#111827",
  },
];

function campaignSlug(row: PublicFormRow): string {
  const raw = String(row.publicSlug || row.form_name || row._id || "form")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
  return raw || "form";
}

export function stripFormShareQuery(url: string): string {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    ["src", "source", "utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach(
      (key) => parsed.searchParams.delete(key)
    );
    return parsed.toString();
  } catch {
    return url.split("?")[0] || url;
  }
}

export function withFormShareQuery(
  url: string,
  params: {
    src?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
  }
): string {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (params.src) parsed.searchParams.set("src", params.src);
    if (params.utmSource) parsed.searchParams.set("utm_source", params.utmSource);
    if (params.utmMedium) parsed.searchParams.set("utm_medium", params.utmMedium);
    if (params.utmCampaign) {
      parsed.searchParams.set("utm_campaign", params.utmCampaign);
    }
    return parsed.toString();
  } catch {
    const bits: string[] = [];
    if (params.src) bits.push(`src=${encodeURIComponent(params.src)}`);
    if (params.utmSource) {
      bits.push(`utm_source=${encodeURIComponent(params.utmSource)}`);
    }
    if (params.utmMedium) {
      bits.push(`utm_medium=${encodeURIComponent(params.utmMedium)}`);
    }
    if (params.utmCampaign) {
      bits.push(`utm_campaign=${encodeURIComponent(params.utmCampaign)}`);
    }
    if (!bits.length) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}${bits.join("&")}`;
  }
}

/** Public URL with no src / UTM (platform links add their own). */
export function buildBarePublicFormUrl(row: PublicFormRow): string {
  return stripFormShareQuery(
    buildPublicFormUrl({ ...row, defaultLeadSource: "" })
  );
}

export function buildFormShareLinks(row: PublicFormRow): FormShareLink[] {
  const bare = buildBarePublicFormUrl(row);
  if (!bare) return [];
  const campaign = campaignSlug(row);
  const defaultSrc = String(row.defaultLeadSource || "").trim();
  return FORM_SHARE_PLATFORMS.map((platform) => {
    if (platform.id === "public") {
      return {
        ...platform,
        url: defaultSrc
          ? withFormShareQuery(bare, { src: defaultSrc })
          : bare,
      };
    }
    return {
      ...platform,
      url: withFormShareQuery(bare, {
        src: platform.src,
        utmSource: platform.utmSource,
        utmMedium: platform.utmMedium,
        utmCampaign: campaign,
      }),
    };
  });
}

function appendSrcQuery(url: string, source?: string): string {
  const src = String(source || "").trim();
  if (!url || !src) return url || "";
  if (/[?&]src=/.test(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}src=${encodeURIComponent(src)}`;
}

function buildScopedPublicUrl(
  row: PublicFormRow,
  pathPrefix: "feedback" | "registration"
): string {
  let url = "";
  if (row.publicUrl && String(row.publicUrl).includes(`/${pathPrefix}/`)) {
    url = String(row.publicUrl);
  } else {
    const base = getPublicWebBase();
    const org = String(row.orgSlug || "").trim();
    const project = String(row.projectSlug || "").trim();
    const form = String(row.publicSlug || row._id || "").trim();
    if (!form) return "";
    if (org && project) url = `${base}/${pathPrefix}/${org}/${project}/${form}`;
    else if (org) url = `${base}/${pathPrefix}/${org}/${form}`;
    else url = `${base}/${pathPrefix}/f/${form}`;
  }
  return appendSrcQuery(url, row.defaultLeadSource);
}

/** Build org/project-scoped public feedback URL. */
export function buildFeedbackPublicUrl(row: PublicFormRow): string {
  if (row.formType && row.formType !== "feedback") return "";
  return buildScopedPublicUrl(row, "feedback");
}

/** Build org/project-scoped public registration URL. */
export function buildRegistrationPublicUrl(row: PublicFormRow): string {
  if (row.formType && row.formType !== "registration") return "";
  return buildScopedPublicUrl(row, "registration");
}

/** Either feedback or registration public URL. */
export function buildPublicFormUrl(row: PublicFormRow): string {
  if (row.formType === "registration") return buildRegistrationPublicUrl(row);
  if (row.formType === "feedback") return buildFeedbackPublicUrl(row);
  return "";
}
